import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  IActivo,
  IDashcam,
  IDispositivoLorawan,
  IFilter,
  ILuminaria,
  IPopulate,
  IPuesta,
  IQueryParam,
  IReporteGenerico,
  IReporteLuminariaACTISEnergia,
  IReporteLuminariaACTISEstado,
  IReporteLuminariaGPE,
  IReporteLuminariaGPEEnergia,
  IReporteLuminariaWellness,
  IReporteTracker4G,
  IReporteTrackerQualcomm,
  IReporteTrackerT1000B,
  ITracker,
  TipoEntidadReporte,
  TipoValoresReporte,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../auxiliares/tabla/tabla.component';
import { IAcciones } from '../../../auxiliares/tabla/tabla.interfaces';
import { AuxiliaresModule } from '../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../auxiliares/dialog/dialog.service';
import { Lista2Service } from '../../../auxiliares/servicios/lista2.service';
import { ReportesGenericosService } from '../../../auxiliares/servicios/http/reportes-genericos.service';
import { fechaCelda, haceCelda } from '../../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../../auxiliares/html-seguro.util';

/** Fecha con segundos para el tooltip: en un reporte los segundos importan. */
const FECHA_CON_SEGUNDOS = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-reportes-genericos',
  templateUrl: './listado-reportes-genericos.component.html',
  styleUrl: './listado-reportes-genericos.component.scss',
})
export class ListadoReportesGenericosComponent
  implements OnInit, OnDestroy, OnChanges
{
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  dialog = inject(MatDialog);
  private dialogService = inject(DialogService);
  private reportesGenericosService = inject(ReportesGenericosService);

  //// Tabla nueva
  @Input() public entidad:
    | ITracker
    | IActivo
    | ILuminaria
    | IPuesta
    | IDispositivoLorawan
    | IDashcam;
  @Input() public tipoValoresReporte: TipoValoresReporte[];
  @Input() public tipoEntidad:
    | TipoEntidadReporte
    | 'Puesta'
    | 'DispositivoLorawan';

  public name = 'ListadoReportesGenericosComponent';
  public reportes?: IReporteGenerico[];
  public reportes$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IReporteGenerico>[];

  // FILTROS

  public search: IRegExpSearch = {
    fields: [],
  };

  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  private fecha: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Fecha',
    tipo: 'dateRange',
  };
  public filtros: IFiltroTabla[] = [this.fecha, this.cliente, this.ancestro];

  private populate: IPopulate[] = [
    //Populo todas las entidades
    {
      path: 'activo',
    },
    {
      path: 'luminaria',
    },
    {
      path: 'tracker',
    },
  ];

  // QUERY
  public query: IQueryParam = {
    sort: '-valores.timestamp',
    // populate: JSON.stringify(this.populate),
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    excludeTotalCount: true,
    childrenLevel: 0,
  };

  //LISTADO
  public async listar(): Promise<void> {
    if (!this.tipoValoresReporte?.length) {
      this.helper.notifError('No se ha especificado el tipo de reporte');
      return;
    }
    if (!this.tipoEntidad) {
      this.helper.notifError('No se ha especificado el tipo de entidad');
      return;
    }
    if (!this.entidad) {
      this.helper.notifError('No se ha especificado la entidad');
      return;
    }

    const filter: IFilter<IReporteGenerico> = JSON.parse(
      this.query.filter || '{}',
    );

    // filter.tipoEntidad = this.tipoEntidad;
    // Tracker, Dashcam y DispositivoLorawan guardan el reporte con idEntidad = _id del nodo.
    // Luminaria, Puesta y grupos matchean por idsAsignados (que ahora incluye idPuesta y grupos de puesta).
    if (
      this.tipoEntidad === 'Tracker' ||
      this.tipoEntidad === 'Dashcam' ||
      this.tipoEntidad === 'DispositivoLorawan'
    ) {
      filter.idEntidad = this.entidad?._id;
    } else {
      filter.idsAsignados = this.entidad?._id; //Acá busca que en idsAsignados esté el id de la luminaria/puesta
    }

    this.query.filter = JSON.stringify(filter);

    // En vistas agregadas se popula la luminaria para mostrar de cuál es el reporte.
    if (this.tipoEntidad === 'Puesta') {
      this.query.populate = JSON.stringify([{ path: 'luminaria' }]);
    }

    // Listar
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .reportesGenericos(this.tipoValoresReporte, this.query, 30)
      .subscribe((data) => {
        const limit = +(this.query.limit || 10);
        const page = +(this.query.page || 0);
        const datos = data.datos || [];
        // El +1 es para forzar el refresco del paginado, ya que el backend no devuelve totalCount
        this.totalCount =
          datos.length === limit
            ? (page + 1) * limit + 1
            : page * limit + datos.length;
        this.reportes = datos;
        // Recomputar columnas: la de Acciones depende de si los datos traen
        // geojson (acción Ubicar), dato que recién se conoce acá
        this.setColumnas();
      });
  }

  // Tabla
  public async eliminar(reporteDispositivo: IReporteGenerico) {
    const res = await this.dialogService.confirm(
      'Confirmá la acción',
      '¿Querés eliminar el reporte?',
    );
    if (!res) return;
    try {
      await this.reportesGenericosService.delete(reporteDispositivo._id!);
      this.helper.notifSuccess('Eliminado correctamente');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  private setColumnas() {
    const columnas: IColumnas<IReporteGenerico>[] = [
      // Valores.timestamp = En formato 24hs
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'valores.timestamp',
        },
        row: {
          html: (dato) => this.fechaReporteHtml(dato.valores?.timestamp),
          tooltip: (dato) => this.fechaConSegundos(dato.valores?.timestamp),
          noData: 'Sin fecha',
        },
      },
    ];

    // En vistas agregadas (puesta / grupo) el listado mezcla reportes de varias
    // luminarias: se agrega una columna que identifica a cuál corresponde.
    if (this.tipoEntidad === 'Puesta') {
      columnas.push({
        header: { label: 'Luminaria' },
        row: {
          parse: (dato) => this.nombreLuminariaReporte(dato),
          noData: 'Sin luminaria',
        },
      });
    }

    // LUMINARIAS GPE PERIODICO
    if (this.tipoValoresReporte.includes('Luminaria GPE Periódico')) {
      columnas.push(
        //Encendido
        {
          header: {
            label: 'Encendido',
          },
          row: {
            estado: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              return valores?.turnOnOffStatus
                ? { texto: 'Encendido', tono: 'ok', icono: 'lightbulb' }
                : { texto: 'Apagado', tono: 'neutro', icono: 'light_off' };
            },
            noData: 'Sin estado de encendido',
          },
        },

        //Voltaje
        {
          header: {
            label: 'Voltaje',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              const voltaje = valores?.voltaje;
              return this.fmtNum(voltaje, 'V');
            },
            noData: 'Sin voltaje asignado',
          },
        },

        //Potencia
        {
          header: {
            label: 'Potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              const potencia = valores?.potencia;
              return this.fmtNum(potencia, 'W');
            },
            noData: 'Sin potencia asignada',
          },
        },

        //Modo
        {
          header: {
            label: 'Modo',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaGPE;
              return valores?.modo;
            },
            noData: 'Sin modo asignado',
          },
        },

        //Tiempo Encendida
        {
          header: {
            label: 'Tiempo encendida',
            sortable: false,
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              const horas = valores?.tiempoEncendida;
              if (horas === null || isNaN(horas)) return '';
              return `${Math.trunc(horas)} h`; // horas enteras, tal cual
            },
            noData: 'Sin calcular',
          },
        },

        // Frame counter
        {
          header: {
            label: 'FCnt',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaGPE;
              if (valores.fCnt || valores.fCnt === 0) {
                return valores.fCnt;
              }
              return '';
            },
            noData: 'Sin información',
          },
        },

        //Energía externa
        {
          header: {
            label: 'Energía externa',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              return valores?.energiaExterna ? 'Sí' : 'No';
            },
            noData: 'Sin energía externa asignada',
          },
        },

        //Estado relé
        {
          header: {
            label: 'Estado relé',
          },
          row: {
            estado: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              return valores?.estadoRele
                ? { texto: 'Prendido', tono: 'ok', icono: 'toggle_on' }
                : { texto: 'Apagado', tono: 'neutro', icono: 'toggle_off' };
            },
            noData: 'Sin estado de relé asignado',
          },
        },

        //Dimmer habilitado
        {
          header: {
            label: 'Dimmer habilitado',
          },
          row: {
            estado: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              return valores?.dimmerHabilitado
                ? { texto: 'Habilitado', tono: 'ok', icono: 'toggle_on' }
                : { texto: 'Deshabilitado', tono: 'neutro', icono: 'toggle_off' };
            },
            noData: 'Sin dimmer habilitado asignado',
          },
        },

        //Dimming
        {
          header: {
            label: 'Dimming',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              return this.fmtNum(valores?.dimmingValue, '%');
            },
            noData: 'Sin dimming',
          },
        },

        // Alarmas
        {
          header: {
            label: 'Alarmas',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPE;
              const alarmas = valores?.alarmas;
              return alarmas?.join(', ');
            },
            noData: 'Sin alarmas',
          },
        },
      );
    }

    // LUMINARIAS GPE ENERGÍA
    if (this.tipoValoresReporte.includes('Luminaria GPE Energía')) {
      columnas.push(
        // Corriente
        {
          header: {
            label: 'Corriente',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const corriente = valores?.corriente;
              return this.fmtNum(corriente, 'mA');
            },
            noData: 'Sin información',
          },
        },
        // Voltaje
        {
          header: {
            label: 'Voltaje',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const voltaje = valores?.voltaje;
              return this.fmtNum(voltaje, 'V');
            },
            noData: 'Sin información',
          },
        },
        // Potencia
        {
          header: {
            label: 'Potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const potencia = valores?.potencia;
              return this.fmtNum(potencia, 'W');
            },
            noData: 'Sin información',
          },
        },
        //Energía
        {
          header: {
            label: 'Energía',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const energia = valores?.energia;
              return this.fmtNum(energia, 'kWh');
            },
            noData: 'Sin información',
          },
        },
        //Energía
        {
          header: {
            label: 'Energía acumulada',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const energiaAcumulada = valores?.energiaTotal;
              return this.fmtNum(energiaAcumulada, 'kWh');
            },
            noData: 'Sin información',
          },
        },
        //Factor de potencia
        {
          header: {
            label: 'Factor de potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaGPEEnergia;
              const factorPotencia = valores?.factorPotencia;
              return this.fmtNum(factorPotencia);
            },
            noData: 'Sin información',
          },
        },
      );
    }

    // LUMINARIAS ACTIS PERIODICO (ESTADO)
    if (this.tipoValoresReporte.includes('Luminaria ACTIS FING Estado')) {
      columnas.push(
        //Encendido
        {
          header: {
            label: 'Encendido',
          },
          row: {
            estado: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEstado;
              return valores?.turnOnOffStatus
                ? { texto: 'Encendido', tono: 'ok', icono: 'lightbulb' }
                : { texto: 'Apagado', tono: 'neutro', icono: 'light_off' };
            },
            noData: 'Sin estado de encendido',
          },
        },

        //Modo
        {
          header: {
            label: 'Modo',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaACTISEstado;
              const text = valores?.modo;
              return text;
            },
            noData: 'Sin modo asignado',
          },
        },

        //Tiempo Encendida
        {
          header: {
            label: 'Tiempo encendida',
            sortable: false,
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEstado;
              const horas = valores?.tiempoEncendida;
              if (horas === null || isNaN(horas)) return '';
              return `${Math.trunc(horas)} h`; // horas enteras, tal cual
            },
            noData: 'Sin calcular',
          },
        },

        // Frame counter
        {
          header: {
            label: 'FCnt',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaACTISEstado;
              if (valores.fCnt || valores.fCnt === 0) {
                return valores.fCnt;
              }
              return '';
            },
            noData: 'Sin información',
          },
        },

        //Dimming
        {
          header: {
            label: 'Dimming',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEstado;
              return this.fmtNum(valores?.dimmingValue, '%');
            },
            noData: 'Sin dimming',
          },
        },
      );
    }

    // LUMINARIAS ACTIS ENERGÍA
    if (this.tipoValoresReporte.includes('Luminaria ACTIS FING Energía')) {
      columnas.push(
        // Corriente
        {
          header: {
            label: 'Corriente',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEnergia;
              const corriente = valores?.corriente;
              return this.fmtNum(corriente, 'mA');
            },
            noData: 'Sin información',
          },
        },
        // Voltaje
        {
          header: {
            label: 'Voltaje',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEnergia;
              const voltaje = valores?.voltaje;
              return this.fmtNum(voltaje, 'V');
            },
            noData: 'Sin información',
          },
        },
        // Potencia
        {
          header: {
            label: 'Potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEnergia;
              const potencia = valores?.potencia;
              return this.fmtNum(potencia, 'W');
            },
            noData: 'Sin información',
          },
        },
        //Factor de potencia
        {
          header: {
            label: 'Factor de potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaACTISEnergia;
              const factorPotencia = valores?.factorPotencia;
              return this.fmtNum(factorPotencia);
            },
            noData: 'Sin información',
          },
        },

        // Alarmas
        {
          header: {
            label: 'Alarmas',
          },
          row: {
            parse: (dato) => {
              const valores = dato?.valores as
                | IReporteLuminariaACTISEnergia
                | undefined;
              const alarmas = valores?.alarmas;

              if (!Array.isArray(alarmas) || alarmas.length === 0) {
                return '';
              }

              return alarmas.join(', ');
            },
            noData: 'Sin alarmas',
          },
        },
      );
    }

    // LUMINARIAS WELLNESS (Deprecates, no se van a usar más)
    if (
      this.tipoValoresReporte.includes('Luminaria Wellness') ||
      (this.entidad as ILuminaria)?.dispositivo?.tipo === 'Luminaria Wellness'
    ) {
      columnas.push(
        // Fecha Creación
        {
          header: {
            label: 'Fecha de creación',
            sort: 'fechaCreacion',
            sortable: true,
          },
          row: {
            html: (dato) => this.fechaReporteHtml(dato.fechaCreacion),
            tooltip: (dato) => this.fechaConSegundos(dato.fechaCreacion),
            noData: 'Sin fecha de creación',
          },
        },

        //Encendido
        {
          header: {
            label: 'Encendido',
          },
          row: {
            estado: (dato) => {
              const valores = dato.valores as IReporteLuminariaWellness;
              return valores?.turnOnOffStatus
                ? { texto: 'Encendido', tono: 'ok', icono: 'lightbulb' }
                : { texto: 'Apagado', tono: 'neutro', icono: 'light_off' };
            },
            noData: 'Sin estado de encendido',
          },
        },

        //Voltaje
        {
          header: {
            label: 'Voltaje',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaWellness;
              const voltaje = valores?.voltage;
              return this.fmtNum(voltaje, 'V');
            },
            noData: 'Sin voltaje asignado',
          },
        },

        //Potencia
        {
          header: {
            label: 'Potencia',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaWellness;
              const potencia = valores?.current * valores?.voltage;
              return this.fmtNum(potencia, 'W');
            },
            noData: 'Sin potencia asignada',
          },
        },

        //Modo
        {
          header: {
            label: 'Modo',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaWellness;
              return valores?.modo;
            },
            noData: 'Sin modo asignado',
          },
        },

        // Frame counter
        {
          header: {
            label: 'FCnt',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaGPE;
              if (valores.fCnt || valores.fCnt === 0) {
                return valores.fCnt;
              }
              return '';
            },
            noData: 'Sin información',
          },
        },

        //Modo forzado
        {
          header: {
            label: 'Modo forzado',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as IReporteLuminariaWellness;
              return valores?.modoForzado;
            },
            noData: 'Sin modo forzado',
          },
        },

        //Dimming
        {
          header: {
            label: 'Dimming',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteLuminariaWellness;
              return this.fmtNum(valores?.dimmingValue, '%');
            },
            noData: 'Sin dimming',
          },
        },
      );
    }

    // TRACKERS (Qualcomm, T1000B, 4G) - Columnas consolidadas. La posición de
    // las dashcams ('Dashcam 4G') comparte las columnas de posición.
    const hasTrackerType =
      this.tipoValoresReporte.includes('Tracker Qualcomm') ||
      this.tipoValoresReporte.includes('Tracker T1000B') ||
      this.tipoValoresReporte.includes('Tracker 4G') ||
      this.tipoValoresReporte.includes('Dashcam 4G');

    if (hasTrackerType) {
      // Coordenadas
      columnas.push({
        header: {
          label: 'Coordenadas',
        },
        row: {
          parse(dato) {
            const valores = dato.valores as
              | IReporteTrackerQualcomm
              | IReporteTrackerT1000B
              | IReporteTracker4G;
            if (!valores?.geojson) return '';
            return (
              valores?.geojson?.coordinates?.[0] +
              ',' +
              valores?.geojson?.coordinates?.[1]
            );
          },
          noData: 'Sin información',
        },
      });

      // Dirección
      columnas.push({
        header: {
          label: 'Dirección',
        },
        row: {
          parse(dato) {
            const valores = dato.valores as
              | IReporteTrackerQualcomm
              | IReporteTrackerT1000B
              | IReporteTracker4G;
            return HelperService.extraerDireccion(valores.direccion);
          },
          tooltip(dato) {
            const valores = dato.valores as
              | IReporteTrackerQualcomm
              | IReporteTrackerT1000B
              | IReporteTracker4G;
            return valores.direccion; // Tooltip con la dirección completa
          },
          noData: 'Sin información',
        },
      });

      // Velocidad
      columnas.push({
        header: {
          label: 'Velocidad',
        },
        row: {
          parse: (dato) => {
            const valores = dato.valores as
              | IReporteTrackerQualcomm
              | IReporteTrackerT1000B
              | IReporteTracker4G;
            return this.fmtNum(valores?.velocidad, 'km/h');
          },
          noData: 'Sin información',
        },
      });

      // Orientación
      columnas.push({
        header: {
          label: 'Orientación',
        },
        row: {
          parse(dato) {
            const valores = dato.valores as
              | IReporteTrackerQualcomm
              | IReporteTrackerT1000B
              | IReporteTracker4G;
            const orientacion = valores.orientacion;
            return HelperService.getOrientacion(orientacion);
          },
          noData: 'Sin información',
        },
      });

      // Batería (solo para Qualcomm y T1000B)
      if (
        this.tipoValoresReporte.includes('Tracker Qualcomm') ||
        this.tipoValoresReporte.includes('Tracker T1000B')
      ) {
        columnas.push({
          header: {
            label: 'Batería',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as
                | IReporteTrackerQualcomm
                | IReporteTrackerT1000B;
              return this.fmtNum(valores?.bateria, '%');
            },
            noData: 'Sin información',
          },
        });

        // Tipo de triangulación
        columnas.push({
          header: {
            label: 'Tipo de triangulación',
          },
          row: {
            parse(dato) {
              const valores = dato.valores as
                | IReporteTrackerQualcomm
                | IReporteTrackerT1000B;
              return valores.tipoTriangulacion;
            },
            noData: 'Sin información',
          },
        });
      }

      // Odómetro (solo para 4G)
      if (this.tipoValoresReporte.includes('Tracker 4G')) {
        columnas.push({
          header: {
            label: 'Odómetro',
          },
          row: {
            parse: (dato) => {
              const valores = dato.valores as IReporteTracker4G;
              if (valores?.odometro == null) return undefined;
              // Hasta 1 decimal, sin «,0» en los enteros
              const km = (valores.odometro / 1000).toLocaleString('es-AR', {
                maximumFractionDigits: 1,
              });
              return `${km} km`;
            },
            noData: 'Sin información',
          },
        });
      }
    }

    // Acciones: se arma condicionalmente y la columna solo se agrega si queda
    // con alguna acción, para no mostrar una columna vacía a clientes sin
    // permisos (ej: reportes de luminaria sin geojson y sin permiso de borrar)
    const acciones: IAcciones<IReporteGenerico>[] = [];

    // Ubicar: solo si algún reporte trae geojson (típicamente trackers)
    const hayGeojson = this.reportes?.some(
      (r) => (r.valores as any)?.geojson?.coordinates,
    );
    if (hayGeojson) {
      acciones.push({
        tipo: 'material',
        color: 'primary',
        icon: 'map',
        tooltip: 'Ubicar',
        oculta(dato) {
          return !(dato.valores as any)?.geojson?.coordinates;
        },
        click: (dato) => {
          const geojson = (dato.valores as any)?.geojson;
          this.helper.ubicar(geojson, 'tracker');
        },
      });
    }

    // Eliminar: solo para nivel 0 (admin)
    if (this.helper.esNivel0()) {
      acciones.push({
        tipo: 'material',
        color: 'warn',
        icon: 'delete',
        tooltip: 'Eliminar',
        click: (dato) => this.eliminar(dato),
      });
    }

    if (acciones.length) {
      columnas.push({
        header: {
          label: 'Acciones',
        },
        row: { acciones },
      });
    }

    this.columnas = columnas;
  }

  // Identificación de la luminaria del reporte (populate 'luminaria' por idsAsignados).
  private nombreLuminariaReporte(dato: IReporteGenerico): string {
    const lum = (dato as unknown as { luminaria?: ILuminaria | ILuminaria[] })
      .luminaria;
    const luminaria = Array.isArray(lum) ? lum[0] : lum;
    return luminaria?.identificacion || '';
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnChanges() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.reportes$?.unsubscribe();
  }

  /**
   * Magnitudes eléctricas para mostrar: coma decimal y como máximo 2 decimales
   * (V × I daba cosas como 5.196800000000005). Sin dato devuelve undefined para
   * que la columna muestre su `noData`.
   */
  private fmtNum(
    valor: number | null | undefined,
    unidad?: string,
  ): string | undefined {
    if (valor == null || Number.isNaN(Number(valor))) return undefined;
    const texto = Number(valor).toLocaleString('es-AR', {
      maximumFractionDigits: 2,
    });
    return unidad ? `${texto} ${unidad}` : texto;
  }

  /** Fecha del reporte (24 h) y "hace x" debajo; los segundos, en el tooltip. */
  private fechaReporteHtml(v: string | Date | undefined | null): string {
    const fecha = fechaCelda(v);
    if (!fecha) return '';
    return `<span class="tabla-fecha"><span>${escaparHtml(fecha)}</span><span class="tabla-secundario">${escaparHtml(haceCelda(v))}</span></span>`;
  }

  private fechaConSegundos(
    v: string | Date | undefined | null,
  ): string | undefined {
    if (!v) return undefined;
    const d = new Date(v);
    if (isNaN(d.getTime())) return undefined;
    return FECHA_CON_SEGUNDOS.format(d).replace(',', '');
  }
}
